import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  ACCOUNT_KEY_GROUP_LABELS,
  ACCOUNT_KEY_GROUP_ORDER,
  type AccountKeyGroup,
  type AccountMappingRow,
} from '@dcc-erp/shared';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import { usePermissions } from '../lib/auth/usePermissions';
import {
  useAccountMappings,
  useActiveCompanyId,
  useCoa,
  useUpdateAccountMapping,
} from '../lib/queries';

export const Route = createFileRoute('/settings/account-mapping')({
  component: AccountMappingPage,
});

export function AccountMappingPage() {
  const companyId = useActiveCompanyId();
  const { data: rows = [], isPending } = useAccountMappings(companyId);
  const { data: coa = [] } = useCoa(companyId, true);
  const update = useUpdateAccountMapping(companyId);
  const { can } = usePermissions();
  const canManage = can('account-mapping.manage');

  const [q, setQ] = useState('');
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [errKey, setErrKey] = useState<{ key: string; msg: string } | null>(null);

  const coaOptions = useMemo(
    () => coa.map((c) => ({ value: c.code, label: `${c.code} — ${c.nameTh}` })),
    [coa],
  );
  const coaLabel = useMemo(() => {
    const m = new Map(coa.map((c) => [c.code, `${c.code} — ${c.nameTh}`]));
    return (code: string) => m.get(code) ?? code;
  }, [coa]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        r.label.toLowerCase().includes(needle) ||
        r.key.toLowerCase().includes(needle) ||
        r.accountCode.includes(needle),
    );
  }, [rows, q]);

  const byGroup = useMemo(() => {
    const g = new Map<AccountKeyGroup, AccountMappingRow[]>();
    for (const r of filtered) {
      const list = g.get(r.group) ?? [];
      list.push(r);
      g.set(r.group, list);
    }
    return g;
  }, [filtered]);

  const configured = rows.filter((r) => r.source === 'configured').length;
  const unset = rows.filter((r) => r.source === 'unset').length;
  const invalid = rows.filter((r) => r.accountCode && !r.valid).length;

  function save(key: string, accountCode: string) {
    if (!accountCode) return;
    setErrKey(null);
    update.mutate(
      { key, data: { accountCode } },
      {
        onSuccess: () => {
          setSavedKey(key);
          setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 2000);
        },
        onError: (e: unknown) =>
          setErrKey({ key, msg: e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ' }),
      },
    );
  }

  return (
    <div className="page">
      <PageHead
        crumbs={['ระบบ', 'ตั้งค่าบัญชีอ้างอิง']}
        title="ตั้งค่าบัญชีอ้างอิง (Account Mapping)"
        actions={
          <>
            <HelpButton guide="settings-account-mapping" />
            <input
              className="input w-sm"
              placeholder="ค้นหา ชื่อบัญชีอ้างอิง / key / รหัส"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </>
        }
      />

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <div className="meta">บัญชีอ้างอิงทั้งหมด</div>
          <div className="num">{rows.length}</div>
          <div className="meta">รายการ</div>
        </div>
        <div className="kpi">
          <div className="meta">ตั้งค่าแล้ว</div>
          <div className="num" style={{ color: 'var(--positive)' }}>
            {configured}
          </div>
          <div className="meta">มีบัญชีผูกไว้</div>
        </div>
        <div className="kpi">
          <div className="meta">ยังไม่ได้ตั้งค่า</div>
          <div className="num" style={{ color: unset ? 'var(--warning)' : undefined }}>
            {unset}
          </div>
          <div className="meta">ระบบจะลงบัญชีไม่ได้</div>
        </div>
        <div className="kpi">
          <div className="meta">รหัสไม่ถูกต้อง</div>
          <div className="num" style={{ color: invalid ? 'var(--negative)' : undefined }}>
            {invalid}
          </div>
          <div className="meta">ไม่มี/ปิดใช้งานใน COA</div>
        </div>
      </div>

      <div
        style={{
          padding: '12px 16px',
          background: 'var(--info-bg, #eff6ff)',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 14,
        }}
      >
        ตารางนี้กำหนดว่า "บัญชีอ้างอิง" แต่ละตัว (เช่น เงินฝากธนาคารหลัก, เจ้าหนี้การค้า, ภาษีขาย)
        ผูกกับ <b>รหัสบัญชีจริงในผังบัญชี (8 หลัก)</b> ตัวไหน — ระบบจะใช้ค่าเหล่านี้ตอนลงบัญชี (JV)
        อัตโนมัติทุกโมดูล แก้แล้วมีผลทันที
      </div>

      {isPending ? (
        <div className="muted" style={{ padding: 24 }}>
          กำลังโหลด…
        </div>
      ) : (
        ACCOUNT_KEY_GROUP_ORDER.filter((g) => byGroup.has(g)).map((group) => (
          <div key={group} style={{ marginBottom: 20 }}>
            <div className="section-title" style={{ margin: '0 0 8px' }}>
              {ACCOUNT_KEY_GROUP_LABELS[group]}
            </div>
            <div className="tbl-shell">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: '38%' }}>บัญชีอ้างอิง</th>
                    <th style={{ width: '46%' }}>รหัสบัญชีในผังบัญชี (COA)</th>
                    <th style={{ width: '16%' }}>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {(byGroup.get(group) ?? []).map((r) => (
                    <tr key={r.key}>
                      <td>
                        <div>{r.label}</div>
                        <div className="meta code" style={{ fontSize: 12 }}>
                          {r.key}
                        </div>
                      </td>
                      <td>
                        {canManage ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ minWidth: 320 }}>
                              <SearchSelect
                                value={r.accountCode}
                                onChange={(code) => save(r.key, code)}
                                options={coaOptions}
                                getLabel={coaLabel}
                                placeholder="— เลือกบัญชีจากผังบัญชี —"
                              />
                            </div>
                            {savedKey === r.key && (
                              <span className="badge positive" style={{ fontSize: 12 }}>
                                บันทึกแล้ว
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="code">
                            {r.accountCode || '—'}
                            {r.accountName ? ` — ${r.accountName}` : ''}
                          </span>
                        )}
                        {errKey?.key === r.key && (
                          <div
                            className="meta"
                            style={{ color: 'var(--negative)', fontSize: 12, marginTop: 4 }}
                          >
                            {errKey.msg}
                          </div>
                        )}
                      </td>
                      <td>
                        {r.source === 'unset' ? (
                          <span className="badge warning">ยังไม่ตั้งค่า</span>
                        ) : r.valid ? (
                          <span className="badge positive">ใช้งานได้</span>
                        ) : (
                          <span className="badge negative">รหัสไม่ถูกต้อง</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
